import { useMemo, useState } from "react";
import { ChevronDown, CalendarIcon, X } from "lucide-react";
import type { DateRange } from "react-day-picker";
import type { Pedido } from "../../types/pedido";
import { PedidoCard } from "./PedidoCard";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface PedidosListProps {
  pedidos: Pedido[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelect: (pedido: Pedido) => void;
}

const PAGE_SIZE_INITIAL = 50;
const PAGE_SIZE_MORE = 30;

type StatusFilter = 'todos' | 'abierto' | 'cerrado' | 'reportes';

function getPedidoStatus(pedido: Pedido): 'abierto' | 'cerrado' | 'reportes' {
  const isCerrado = pedido.CERRAR_PRUEBA === true;
  const hasBitacora = !!pedido.BITACORA_LINK;
  const hasCliente = !!pedido.CLIENTE_LINK;
  if (isCerrado && hasBitacora && hasCliente) return 'reportes';
  if (isCerrado) return 'cerrado';
  return 'abierto';
}

function formatDate(date: Date) {
  return date.toLocaleDateString('es-MX');
}

export function PedidosList({ pedidos, searchTerm, onSearchChange, onSelect }: PedidosListProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE_INITIAL);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [pruebaFilter, setPruebaFilter] = useState('');
  const [rango, setRango] = useState<DateRange | undefined>();

  const pruebasDisponibles = useMemo(() => {
    const nombres = new Set<string>();
    pedidos.forEach(p => p.pruebasSolicitadas.forEach(pr => {
      if (pr.nombre) nombres.add(pr.nombre);
    }));
    return Array.from(nombres).sort((a, b) => a.localeCompare(b));
  }, [pedidos]);

  const filteredPedidos = pedidos
    .filter(p => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matches =
          p.numeroPedido?.toString().toLowerCase().includes(term) ||
          p.pruebasSolicitadas.some(pr => pr.nombre?.toLowerCase().includes(term)) ||
          p.articulos?.some(a => a.toLowerCase().includes(term));
        if (!matches) return false;
      }
      if (statusFilter !== 'todos' && getPedidoStatus(p) !== statusFilter) return false;
      if (pruebaFilter && !p.pruebasSolicitadas.some(pr => pr.nombre === pruebaFilter)) return false;
      if (rango?.from && p.fechaEntrega) {
        const fecha = new Date(p.fechaEntrega);
        if (fecha < rango.from) return false;
        if (rango.to) {
          const hasta = new Date(rango.to);
          hasta.setHours(23, 59, 59);
          if (fecha > hasta) return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      const extractNumber = (pedido: string) => {
        const match = pedido.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
      };
      return extractNumber(b.numeroPedido || '') - extractNumber(a.numeroPedido || '');
    });

  const visiblePedidos = filteredPedidos.slice(0, visibleCount);
  const hasMore = visibleCount < filteredPedidos.length;

  const hasFilters = searchTerm || statusFilter !== 'todos' || pruebaFilter || rango?.from;

  const handleReset = () => {
    onSearchChange('');
    setStatusFilter('todos');
    setPruebaFilter('');
    setRango(undefined);
    setVisibleCount(PAGE_SIZE_INITIAL);
  };

  const rangoLabel = rango?.from
    ? rango.to
      ? `${formatDate(rango.from)} — ${formatDate(rango.to)}`
      : formatDate(rango.from)
    : 'Fecha de emisión';

  return (
    <div className="space-y-6">
      <h1>Pedidos</h1>
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Buscar por No. de Pedido"
          value={searchTerm}
          onChange={e => { onSearchChange(e.target.value); setVisibleCount(PAGE_SIZE_INITIAL); }}
          className="border rounded-xl px-3 py-2 w-full max-w-xs"
        />

        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 border rounded-xl px-3 py-2 text-sm text-left min-w-[220px]">
              <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className={rango?.from ? '' : 'text-muted-foreground'}>{rangoLabel}</span>
              {rango?.from && (
                <X
                  className="w-3 h-3 ml-auto text-muted-foreground hover:text-foreground"
                  onClick={e => { e.stopPropagation(); setRango(undefined); setVisibleCount(PAGE_SIZE_INITIAL); }}
                />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={rango}
              onSelect={r => { setRango(r); setVisibleCount(PAGE_SIZE_INITIAL); }}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>

        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value as StatusFilter); setVisibleCount(PAGE_SIZE_INITIAL); }}
          className="border rounded-xl px-3 py-2 text-sm bg-transparent"
        >
          <option value="todos">Todos</option>
          <option value="abierto">Abierto</option>
          <option value="cerrado">Cerrado</option>
          <option value="reportes">Reportes</option>
        </select>

        <select
          value={pruebaFilter}
          onChange={e => { setPruebaFilter(e.target.value); setVisibleCount(PAGE_SIZE_INITIAL); }}
          className="border rounded-xl px-3 py-2 text-sm bg-transparent max-w-xs"
        >
          <option value="">Todas las pruebas</option>
          {pruebasDisponibles.map(nombre => (
            <option key={nombre} value={nombre}>{nombre}</option>
          ))}
        </select>

        {hasFilters && (
          <button onClick={handleReset} className="text-sm text-muted-foreground underline">
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visiblePedidos.map((pedido) => (
          <PedidoCard key={pedido.id} pedido={pedido} onClick={onSelect} />
        ))}
      </div>

      <div className="flex flex-col items-center gap-2 pt-2">
        <p className="text-sm text-muted-foreground">
          Mostrando {visiblePedidos.length} de {filteredPedidos.length} pedidos
        </p>
        {hasMore && (
          <button
            onClick={() => setVisibleCount(prev => prev + PAGE_SIZE_MORE)}
            className="flex items-center gap-2 px-4 py-2 text-sm border rounded hover:bg-gray-50 transition-colors"
          >
            <ChevronDown className="w-4 h-4" />
            Cargar 30 más
          </button>
        )}
      </div>
    </div>
  );
}
